import { useMemo, useState, useRef, useEffect } from "react";
import { toISO, fromISO, addDays, DOW_SHORT, MONTH_SHORT } from "@/lib/dates";

const ABSENCE_COLOR = {
  holiday: "bg-amber-400",
  sickness: "bg-rose-500",
  absence: "bg-zinc-700",
};

/**
 * Compact yearly heatmap: staff (rows) × every day (columns).
 * - Working days (Tue-Fri) rendered on white
 * - Non-working days (Sat/Sun/Mon) rendered on light zinc
 * - Absence days rendered in colour of the absence type
 * - Sticky staff column and sticky month header
 */
export default function StaffYearGrid({
  staff,
  absences,
  startDate,
  endDate,
  onCellClick,
}) {
  const [focusedMonth, setFocusedMonth] = useState(null); // {year, month} | null

  const allDays = useMemo(() => {
    const out = [];
    let cur = fromISO(startDate);
    const end = fromISO(endDate);
    while (cur <= end) {
      out.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return out;
  }, [startDate, endDate]);

  const days = useMemo(() => {
    if (!focusedMonth) return allDays;
    return allDays.filter(
      (d) => d.getFullYear() === focusedMonth.year && d.getMonth() === focusedMonth.month
    );
  }, [allDays, focusedMonth]);

  const months = useMemo(() => {
    const groups = [];
    let currentKey = null;
    let currentGroup = null;
    allDays.forEach((d, idx) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== currentKey) {
        currentKey = key;
        currentGroup = {
          label: MONTH_SHORT[d.getMonth()],
          year: d.getFullYear(),
          monthIndex: d.getMonth(),
          length: 0,
          startIdx: idx,
        };
        groups.push(currentGroup);
      }
      currentGroup.length += 1;
    });
    return groups;
  }, [allDays]);

  const absenceByStaffDate = useMemo(() => {
    const m = {};
    for (const ab of absences) {
      if (!m[ab.staff_id]) m[ab.staff_id] = {};
      let cur = fromISO(ab.start_date);
      const end = fromISO(ab.end_date);
      while (cur <= end) {
        m[ab.staff_id][toISO(cur)] = ab.type;
        cur = addDays(cur, 1);
      }
    }
    return m;
  }, [absences]);

  const [hover, setHover] = useState(null); // {staff, date, absenceType}
  const scrollRef = useRef(null);
  const CELL_W = focusedMonth ? 32 : 4;
  const CELL_H = focusedMonth ? 26 : 20;
  const NAME_W = 150;
  const HEADER_H = focusedMonth ? 48 : 40;

  const isNonWork = (d) => [0, 1, 6].includes(d.getDay());

  const staffAbsenceCounts = useMemo(() => {
    const c = {};
    for (const s of staff) {
      const perStaff = absenceByStaffDate[s.id] || {};
      const totals = { holiday: 0, sickness: 0, absence: 0 };
      for (const k of Object.keys(perStaff)) {
        totals[perStaff[k]] = (totals[perStaff[k]] || 0) + 1;
      }
      c[s.id] = totals;
    }
    return c;
  }, [staff, absenceByStaffDate]);

  const gridWidth = NAME_W + days.length * CELL_W;

  return (
    <div className="border-2 border-black bg-white" data-testid="staff-year-grid">
      {/* Title / legend bar */}
      <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {focusedMonth && (
            <button
              onClick={() => setFocusedMonth(null)}
              data-testid="year-back-btn"
              className="flex items-center gap-1.5 px-2 py-1 border border-black text-[10px] font-mono-plex tracking-widest hover:bg-zinc-100 active:scale-[0.98]"
            >
              ← YEAR
            </button>
          )}
          <div className="font-heading font-black text-lg tracking-tight leading-none">
            {focusedMonth
              ? `${MONTH_SHORT[focusedMonth.month]} ${focusedMonth.year} · ${staff.length} STAFF`
              : `YEAR VIEW · ${staff.length} STAFF × ${allDays.length} DAYS`}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono-plex tracking-widest text-zinc-600">
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-emerald-400 border border-emerald-700" />
            WORK
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-zinc-200" />
            REST
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-amber-400" />
            HOL
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-rose-500" />
            SIC
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-zinc-700" />
            ABS
          </div>
        </div>
      </div>

      {/* Hover tooltip strip */}
      <div
        className="border-b border-zinc-300 px-4 py-1.5 font-mono-plex text-[10px] tracking-widest text-zinc-600 h-7"
        data-testid="staff-year-hover"
      >
        {hover ? (
          <span>
            <span className="font-bold text-black">{hover.staff.name}</span>
            <span className="mx-2">·</span>
            <span>{hover.date}</span>
            {hover.absenceType && (
              <>
                <span className="mx-2">·</span>
                <span
                  className={`px-1 py-0.5 text-white font-bold ${
                    ABSENCE_COLOR[hover.absenceType]
                  } ${hover.absenceType === "holiday" ? "text-amber-950" : ""}`}
                >
                  {hover.absenceType.toUpperCase()}
                </span>
              </>
            )}
          </span>
        ) : (
          <span className="text-zinc-400">HOVER A CELL FOR DETAILS · CLICK TO LOG ABSENCE</span>
        )}
      </div>

      {/* Scroll container */}
      <div
        ref={scrollRef}
        className="overflow-auto grid-scroll"
        style={{ maxHeight: "62vh" }}
      >
        <div style={{ width: gridWidth, position: "relative" }}>
          {/* Month header row */}
          <div
            className="sticky top-0 z-20 flex bg-white border-b-2 border-black"
            style={{ height: HEADER_H }}
          >
            <div
              className="sticky left-0 z-30 bg-black text-white flex items-center px-3 border-r-2 border-black"
              style={{ width: NAME_W, minWidth: NAME_W, height: HEADER_H }}
            >
              <div className="font-heading font-black text-[10px] tracking-widest">STAFF</div>
            </div>
            {focusedMonth
              ? days.map((d, i) => {
                  const nonWork = isNonWork(d);
                  return (
                    <div
                      key={i}
                      className={`border-r border-zinc-300 flex flex-col justify-center items-center ${
                        nonWork ? "bg-zinc-100 text-zinc-500" : "bg-white"
                      }`}
                      style={{ width: CELL_W, minWidth: CELL_W }}
                    >
                      <div className="font-mono-plex text-[8px] tracking-widest">
                        {DOW_SHORT[d.getDay()]}
                      </div>
                      <div
                        className={`font-heading font-bold text-xs leading-none mt-0.5 ${
                          nonWork ? "text-zinc-500" : ""
                        }`}
                      >
                        {d.getDate()}
                      </div>
                    </div>
                  );
                })
              : months.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setFocusedMonth({ year: m.year, month: m.monthIndex })}
                    data-testid={`year-month-${m.year}-${m.monthIndex}`}
                    className="border-r-2 border-zinc-300 flex flex-col justify-center items-center bg-white hover:bg-emerald-50 focus-visible:bg-emerald-100 transition-colors"
                    style={{ width: m.length * CELL_W, minWidth: m.length * CELL_W }}
                    title={`Focus ${MONTH_SHORT[m.monthIndex]} ${m.year}`}
                  >
                    <div className="font-heading font-black text-[11px] tracking-tight leading-none">
                      {m.label}
                    </div>
                    <div className="font-mono-plex text-[9px] tracking-widest text-zinc-500">
                      {String(m.year).slice(2)}
                    </div>
                  </button>
                ))}
          </div>

          {/* Body rows */}
          {staff.map((s, rowIdx) => {
            const perStaff = absenceByStaffDate[s.id] || {};
            const totals = staffAbsenceCounts[s.id] || { holiday: 0, sickness: 0, absence: 0 };
            const totalAbsences = totals.holiday + totals.sickness + totals.absence;
            const zebra = rowIdx % 2 === 1 ? "bg-zinc-50" : "bg-white";
            return (
              <div
                key={s.id}
                className="flex border-b-2 border-black"
                style={{ height: CELL_H }}
                data-testid={`year-row-${s.id}`}
              >
                <div
                  className={`sticky left-0 z-10 ${zebra} border-r-2 border-black px-2 flex items-center justify-between gap-1`}
                  style={{ width: NAME_W, minWidth: NAME_W }}
                >
                  <span
                    className="font-mono-plex text-[10px] font-medium truncate"
                    title={s.name}
                  >
                    {s.name}
                  </span>
                  {totalAbsences > 0 && (
                    <span className="font-mono-plex text-[8px] tracking-widest text-zinc-500 shrink-0">
                      {totalAbsences}
                    </span>
                  )}
                </div>
                {days.map((d, di) => {
                  const iso = toISO(d);
                  const abs = perStaff[iso];
                  const nonWork = isNonWork(d);
                  const isMonthStart = d.getDate() === 1;
                  const isMonday = d.getDay() === 1;
                  let bg = "bg-emerald-400";
                  if (abs) bg = ABSENCE_COLOR[abs];
                  else if (nonWork) bg = "bg-zinc-200";

                  // Gridlines: bolder at month start, medium at week start (Monday),
                  // solid zinc between every day so cells are always separated.
                  const borderClass = focusedMonth
                    ? "border-r border-zinc-400"
                    : isMonthStart
                    ? "border-l-2 border-black"
                    : isMonday
                    ? "border-l border-zinc-600"
                    : "border-r border-zinc-300";

                  return (
                    <button
                      key={di}
                      data-testid={abs ? `year-cell-${s.id}-${iso}` : undefined}
                      onMouseEnter={() => setHover({ staff: s, date: iso, absenceType: abs })}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => onCellClick?.(s, iso, abs)}
                      className={`${bg} ${borderClass} hover:outline hover:outline-2 hover:outline-black hover:z-10 relative transition-none`}
                      style={{
                        width: CELL_W,
                        minWidth: CELL_W,
                        height: CELL_H,
                        padding: 0,
                      }}
                      title={`${s.name} · ${iso}${abs ? ` · ${abs.toUpperCase()}` : ""}`}
                    />
                  );
                })}
              </div>
            );
          })}
          {staff.length === 0 && (
            <div className="p-6 font-mono-plex text-xs text-zinc-500">NO STAFF</div>
          )}
        </div>
      </div>
    </div>
  );
}
